import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import {
  CloudUpload,
  FileDownload,
  Description,
  History,
  CheckCircle,
  ErrorOutline,
  HourglassBottom
} from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

/* ================= DOCUMENT TYPES ================= */
const DOCUMENT_TYPES = [
  { label: 'ID Proof (Aadhar / PAN)', value: 'ID_PROOF' },
  { label: 'Educational Certificate', value: 'EDUCATION' },
  { label: 'Experience Letter', value: 'EXPERIENCE' },
  { label: 'permarnent adress proof(House Tax, Aadhar)', value: 'ADDRESS_PROOF' },
  {label: 'Current Address Proof (Electricity Bill, Rent Agreement,gas )', value: 'CURRENT_ADDRESS_PROOF' },
  { label: 'Resume / CV', value: 'RESUME' },
  { label: 'Photograph', value: 'PHOTOGRAPH' },
  { label: 'Offer Letter', value: 'OFFER_LETTER' },
  { label: 'Relieving Letter', value: 'RELIEVING_LETTER' },
  { label: 'Salary Slip', value: 'SALARY_SLIP' },
  { label: 'Bank Statement / Passbook', value: 'BANK_STATEMENT' },
  { label: 'Medical Certificate', value: 'MEDICAL_CERTIFICATE' },
  { label: 'DRA Certificate', value: 'DRA_CERTIFICATE' },
  { label: 'Police Verification', value: 'POLICE_VERIFICATION' },
  { label: 'Other Document', value: 'OTHER' }
];

/* =====================================================
   EMPLOYEE DOCUMENT UPLOAD + HR STATUS VIEW
===================================================== */
const EmployeeDocumentUpload = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /* ================= FETCH DOCUMENTS ================= */
  const fetchDocuments = async () => {
    if (!user?.employeeId) return;
    setLoading(true);
    try {
      const res = await api.get(`/documents/employee/${user.employeeId}`);
      setDocuments(res.data);
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [user]);

  /* ================= FILE HANDLING ================= */
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file || !docType) {
      setError('Please select document type and file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/documents/upload/${docType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess('Document uploaded successfully');
      setFile(null);
      setDocType('');
      fetchDocuments();
    } catch {
      setError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (id, fileName) => {
    const res = await api.get(`/documents/download/${id}`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  };

  /* ================= STATUS CHIP ================= */
  const renderStatusChip = (status) => {
    if (status === 'APPROVED') {
      return <Chip color="success" icon={<CheckCircle />} label="Approved" />;
    }
    if (status === 'REJECTED') {
      return <Chip color="error" icon={<ErrorOutline />} label="Rejected" />;
    }
    return <Chip color="warning" icon={<HourglassBottom />} label="Pending" />;
  };

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  /* ================= UI ================= */
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={800} color="primary">
        My Documents
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Upload documents and track HR approval status
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Grid container spacing={4}>
        {/* ================= UPLOAD ================= */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={700} mb={2}>
                <CloudUpload sx={{ mr: 1 }} /> Upload Document
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={docType}
                  label="Document Type"
                  onChange={(e) => setDocType(e.target.value)}
                >
                  {DOCUMENT_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                onClick={() => fileInputRef.current.click()}
                sx={{
                  border: '2px dashed #90caf9',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
              >
                <input hidden type="file" ref={fileInputRef} onChange={handleFileChange} />
                <Description sx={{ fontSize: 50, color: 'primary.main' }} />
                <Typography fontWeight={600}>
                  {file ? file.name : 'Click to select file'}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 3, py: 1.4, fontWeight: 700 }}
                disabled={uploading}
                onClick={handleUpload}
              >
                {uploading ? <CircularProgress size={22} /> : 'Upload'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* ================= DOCUMENT LIST ================= */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ borderRadius: 3 }}>
            <Box sx={{ p: 2.5, bgcolor: 'grey.100', display: 'flex', justifyContent: 'space-between' }}>
              <Typography fontWeight={700}>
                <History sx={{ mr: 1 }} /> Uploaded Documents
              </Typography>
              <Chip label={`${documents.length} Files`} color="primary" />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><b>Type</b></TableCell>
                    <TableCell><b>File</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                    <TableCell><b>HR Remarks</b></TableCell>
                    <TableCell align="right"><b>Download</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        No documents uploaded
                      </TableCell>
                    </TableRow>
                  ) : (
                    documents.map(doc => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Chip
                            label={doc.docType.replaceAll('_', ' ')}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{doc.originalFileName}</TableCell>
                        <TableCell>{renderStatusChip(doc.status)}</TableCell>
                        <TableCell>{doc.hrRemarks || '—'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Download">
                            <IconButton onClick={() => handleDownload(doc.id, doc.originalFileName)}>
                              <FileDownload />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EmployeeDocumentUpload;
